/**
 * FSRNode — TSL implementation of FSR 1 EASU + RCAS.
 *
 * Two TSL nodes:
 *   - `createEASUNode` — 12-tap edge-adaptive spatial upsample
 *   - `createRCASNode` — 3×3 unsharp with neighborhood-clamped lobe
 *
 * Caller composes them in their post chain:
 *   const easu = createEASUNode({ srcColor: scenePassTextureNode, … });
 *   const rcas = createRCASNode({ srcColor: convertToTexture(easu.node), … });
 *   // Bind rcas.node as the final post output
 *
 * `srcColor` must be a texture NODE for anything a pass renders (the pass
 * texture node, an RTT node) — see `sampleAt`. A raw Texture is only right for
 * an image that exists without a producer in the graph.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, vec2, vec3, vec4, float, uniform,
  texture, max, min, mix, clamp, abs, screenUV,
  floor, fract, saturate, sqrt, select,
} from 'three/tsl';

/**
 * Sample `src` at `uv`. A texture NODE (a pass texture node, an RTT node) is
 * sampled through its own `.sample()`, a raw `Texture` through `texture()`.
 *
 * WHY: three renders a PassNode only when a PassTextureNode that points at it
 * is part of the built graph (`PassTextureNode.setup` registers the pass as a
 * node property, and that is what puts its `updateBefore` in the frame). The
 * first FSR build sampled the pass's raw render-target texture with
 * `texture(passTextureNode.value)`, so no node in the graph referenced the
 * pass: the scene was never rendered, EASU read a cleared target, and every
 * tier drew a black frame under a live HUD — 3 triangles per frame, no error
 * on either backend ("now the screen is black", 2026-09-08).
 */
function sampleAt(src: unknown, uv: unknown): any {
  const s = src as { isTextureNode?: boolean; sample?: (uv: unknown) => unknown } | null;
  if (s && s.isTextureNode === true && typeof s.sample === 'function') return s.sample(uv);
  return texture(src as THREE.Texture, uv as never);
}

export interface EASUUniforms {
  srcSize: ReturnType<typeof uniform>;
  dstSize: ReturnType<typeof uniform>;
}

export interface EASUNodeResult {
  node: unknown;
  uniforms: EASUUniforms;
}

/**
 * Build the EASU spatial upsampling node.
 *
 * Reads `srcColor` at the source resolution and outputs the upsampled
 * color at `dstSize` (driven by `screenUV`).
 *
 * The EASU kernel is a 12-tap edge-adaptive interpolator that uses the
 * local gradient to bias samples toward the edge direction, preserving
 * fine detail. Reference: AMD GPUOpen FidelityFX FSR 1.0.
 */
export function createEASUNode(args: {
  srcColor: unknown;
  srcWidth: number;
  srcHeight: number;
  dstWidth: number;
  dstHeight: number;
  /**
   * 'amd' (default since 2026-09-25): AMD FidelityFX FSR 1's real EASU — 12 taps, the local
   * gradient shapes an approximate Lanczos-2 kernel into an ellipse along the edge, anti-ringing
   * clamp to the nearest 2×2 (the math of three r185's FSR1Node, MIT; see `easuAmdCpu`).
   * 'legacy': the first engine kernel (an edge-weighted bilinear with a 0.15 outer-ring blend) —
   * it never sharpened anything, which is why FSR 1 read as a blur.
   */
  kernel?: 'amd' | 'legacy';
}): EASUNodeResult {
  const srcSizeU = uniform(vec2(args.srcWidth, args.srcHeight));
  const dstSizeU = uniform(vec2(args.dstWidth, args.dstHeight));
  if ((args.kernel ?? 'amd') === 'amd') {
    return { node: createEasuAmd(args.srcColor, srcSizeU)(), uniforms: { srcSize: srcSizeU, dstSize: dstSizeU } };
  }

  const easuFn: any = Fn(() => {
    const uvOut = screenUV;
    // Source-space pixel coordinate
    const srcCoord = uvOut.mul(srcSizeU).sub(0.5);
    const srcBase = vec2(
      (srcCoord.x as any).floor(),
      (srcCoord.y as any).floor(),
    );
    const f = vec2(
      srcCoord.x.sub(srcBase.x),
      srcCoord.y.sub(srcBase.y),
    );
    const invSrcSize = vec2(float(1).div(srcSizeU.x), float(1).div(srcSizeU.y));

    // Gather 12 nearby samples (4 cardinals + 4 diagonals + 4 extra for direction estimate)
    const sample = (dx: number, dy: number) => {
      const uv = vec2(
        srcBase.x.add(float(dx)).add(0.5).mul(invSrcSize.x),
        srcBase.y.add(float(dy)).add(0.5).mul(invSrcSize.y),
      );
      return sampleAt(args.srcColor, uv);
    };
    // 12-tap "+" + diagonals pattern
    const c00: any = sample(-1, -1); const c10: any = sample(0, -1); const c20: any = sample(1, -1); const c30: any = sample(2, -1);
    const c01: any = sample(-1,  0); const c11: any = sample(0,  0); const c21: any = sample(1,  0); const c31: any = sample(2,  0);
    const c02: any = sample(-1,  1); const c12: any = sample(0,  1); const c22: any = sample(1,  1); const c32: any = sample(2,  1);

    // Estimate edge direction from luma differences
    const lumaWeight = vec3(0.299, 0.587, 0.114);
    const l11 = c11.rgb.dot(lumaWeight);
    const l21 = c21.rgb.dot(lumaWeight);
    const l12 = c12.rgb.dot(lumaWeight);
    const l22 = c22.rgb.dot(lumaWeight);

    // Cardinal differences for direction estimate
    const dx = abs(l21.sub(l11)).add(abs(l22.sub(l12)));
    const dy = abs(l12.sub(l11)).add(abs(l22.sub(l21)));

    // Edge-aware blend weight: favor along-edge interpolation
    const edgeStr = max(dx, dy);
    const horiz = float(1).sub(clamp(edgeStr.mul(2), float(0), float(1)));

    // Bilinear sample at the 2×2 inner neighborhood
    const m00 = c11.rgb;
    const m10 = c21.rgb;
    const m01 = c12.rgb;
    const m11 = c22.rgb;
    const top = mix(m00, m10, f.x);
    const bot = mix(m01, m11, f.x);
    const baseline = mix(top, bot, f.y);

    // Edge-direction-biased extras (use the outer ring for soft edge enhancement)
    const ring = (c00.rgb.add(c10.rgb).add(c20.rgb).add(c30.rgb)
      .add(c01.rgb).add(c31.rgb)
      .add(c02.rgb).add(c32.rgb)).mul(0.125);

    // Blend: in flat regions favor ring (smoothing); on edges favor baseline (preserve sharp)
    const result = mix(baseline, ring, horiz.mul(0.15));

    // Clamp to local min/max to prevent ringing
    const minRGB = min(min(min(m00, m10), m01), m11);
    const maxRGB = max(max(max(m00, m10), m01), m11);
    const clamped = vec3(
      clamp(result.x, minRGB.x, maxRGB.x),
      clamp(result.y, minRGB.y, maxRGB.y),
      clamp(result.z, minRGB.z, maxRGB.z),
    );

    return vec4(clamped, float(1));
  });

  return {
    node: easuFn(),
    uniforms: { srcSize: srcSizeU, dstSize: dstSizeU },
  };
}

// ─── AMD EASU (FSR 1) ────────────────────────────────────────────────────────
//
// Port of the EASU pass of three r185's `examples/jsm/tsl/display/FSR1Node.js`
// (MIT, itself a port of AMD FidelityFX FSR 1 — MIT) onto this module's
// source-agnostic sampling: taps are read at exact texel CENTRES through
// `sampleAt`, so a pass texture node, an RTT node or a raw texture all work,
// and the source size is a uniform the governor can move without a rebuild.
// `easuAmdCpu` below is the SAME math on the CPU (tests/unit/easu-kernel.test.ts).
//
//        b c
//      e f g h        12 taps around the 2×2 (f g / j k) that holds the
//      i j k l        output sample; luma = r/2 + g + b/2.
//        n o

/** Luma the kernel analyses (AMD's cheap `r·0.5 + g + b·0.5`). */
const easuLuma = (s: any): any => s.r.mul(0.5).add(s.g).add(s.b.mul(0.5));

function createEasuAmd(srcColor: unknown, srcSizeU: ReturnType<typeof uniform>): any {
  return Fn(() => {
    const size = srcSizeU as any;
    const inv = vec2(float(1).div(size.x), float(1).div(size.y)).toConst();
    const pp = screenUV.mul(size).sub(0.5).toConst();
    const fp = floor(pp).toConst();
    const f = fract(pp).toConst();
    const tap = (dx: number, dy: number): any => sampleAt(srcColor, fp.add(vec2(dx + 0.5, dy + 0.5)).mul(inv));
    const sB = tap(0, -1).toConst(), sC = tap(1, -1).toConst();
    const sE = tap(-1, 0).toConst(), sF = tap(0, 0).toConst(), sG = tap(1, 0).toConst(), sH = tap(2, 0).toConst();
    const sI = tap(-1, 1).toConst(), sJ = tap(0, 1).toConst(), sK = tap(1, 1).toConst(), sL = tap(2, 1).toConst();
    const sN = tap(0, 2).toConst(), sO = tap(1, 2).toConst();
    const bL = easuLuma(sB), cL = easuLuma(sC), eL = easuLuma(sE), fL = easuLuma(sF), gL = easuLuma(sG), hL = easuLuma(sH);
    const iL = easuLuma(sI), jL = easuLuma(sJ), kL = easuLuma(sK), lL = easuLuma(sL), nL = easuLuma(sN), oL = easuLuma(sO);

    const dir = vec2(0).toVar();
    const len = float(0).toVar();
    const edge = (w: any, aL: any, b2: any, c2: any, d2: any, e2: any): void => {
      const dc = d2.sub(c2), cb = c2.sub(b2); const dirX = d2.sub(b2);
      const lenX = max(abs(dc), abs(cb)); const sLenX = saturate(abs(dirX).div(max(lenX, float(1 / 65536))));
      dir.x.addAssign(dirX.mul(w)); len.addAssign(sLenX.mul(sLenX).mul(w));
      const ec = e2.sub(c2), ca = c2.sub(aL); const dirY = e2.sub(aL);
      const lenY = max(abs(ec), abs(ca)); const sLenY = saturate(abs(dirY).div(max(lenY, float(1 / 65536))));
      dir.y.addAssign(dirY.mul(w)); len.addAssign(sLenY.mul(sLenY).mul(w));
    };
    const w0 = float(1).sub(f.x).mul(float(1).sub(f.y)), w1 = f.x.mul(float(1).sub(f.y)), w2 = float(1).sub(f.x).mul(f.y), w3 = f.x.mul(f.y);
    edge(w0, bL, eL, fL, gL, jL);
    edge(w1, cL, fL, gL, hL, kL);
    edge(w2, fL, iL, jL, kL, nL);
    edge(w3, gL, jL, kL, lL, oL);

    const dirSq = dir.x.mul(dir.x).add(dir.y.mul(dir.y)).toConst();
    const zro = dirSq.lessThan(1 / 32768).toConst();
    const rDirLen = float(1).div(sqrt(max(dirSq, float(1 / 32768)))).toConst();
    dir.x.assign(select(zro, float(1), dir.x));
    dir.mulAssign(select(zro, float(1), rDirLen));
    len.assign(len.mul(0.5));
    len.mulAssign(len);
    const stretch = dir.x.mul(dir.x).add(dir.y.mul(dir.y)).div(max(abs(dir.x), abs(dir.y))).toConst();
    const len2 = vec2(float(1).add(stretch.sub(1).mul(len)), float(1).sub(len.mul(0.5))).toConst();
    const lob = float(0.5).add(float(1 / 4 - 0.04 - 0.5).mul(len)).toConst();
    const clp = float(1).div(lob).toConst();

    const aC = vec4(0).toVar();
    const aW = float(0).toVar();
    const acc = (ox: number, oy: number, color: any): void => {
      const off = vec2(ox, oy).sub(f);
      const vx = off.x.mul(dir.x).add(off.y.mul(dir.y));
      const vy = off.x.mul(dir.y).negate().add(off.y.mul(dir.x));
      const sx = vx.mul(len2.x), sy = vy.mul(len2.y);
      const d2 = min(sx.mul(sx).add(sy.mul(sy)), clp);
      const wB = d2.mul(2 / 5).sub(1), wA = d2.mul(lob).sub(1);
      const w = wB.mul(wB).mul(25 / 16).sub(25 / 16 - 1).mul(wA.mul(wA));
      aC.addAssign(vec4(color).mul(w)); aW.addAssign(w);
    };
    acc(0, -1, sB); acc(1, -1, sC); acc(-1, 0, sE); acc(0, 0, sF); acc(1, 0, sG); acc(2, 0, sH);
    acc(-1, 1, sI); acc(0, 1, sJ); acc(1, 1, sK); acc(2, 1, sL); acc(0, 2, sN); acc(1, 2, sO);
    const outC = aC.div(aW);
    // anti-ringing: clamp to the min/max of the 2×2 that holds the sample
    const min4 = min(min(sF, sG), min(sJ, sK)), max4 = max(max(sF, sG), max(sJ, sK));
    return vec4(clamp(outC, min4, max4).rgb, float(1));
  });
}

/**
 * CPU mirror of the AMD EASU kernel (same math as `createEasuAmd`). `at(x, y)`
 * returns the source texel [r, g, b] at INTEGER coordinates (clamp at the
 * edges yourself); (u, v) is the output sample in 0..1 over a W×H source.
 */
export function easuAmdCpu(at: (x: number, y: number) => [number, number, number], W: number, H: number, u: number, v: number): [number, number, number] {
  const ppx = u * W - 0.5, ppy = v * H - 0.5; const fx0 = Math.floor(ppx), fy0 = Math.floor(ppy); const fx = ppx - fx0, fy = ppy - fy0;
  const t = (dx: number, dy: number): [number, number, number] => at(fx0 + dx, fy0 + dy);
  const L = (c: [number, number, number]): number => c[0] * 0.5 + c[1] + c[2] * 0.5;
  const sB = t(0, -1), sC = t(1, -1), sE = t(-1, 0), sF = t(0, 0), sG = t(1, 0), sH = t(2, 0), sI = t(-1, 1), sJ = t(0, 1), sK = t(1, 1), sL = t(2, 1), sN = t(0, 2), sO = t(1, 2);
  let dx = 0, dy = 0, len = 0;
  const edge = (w: number, a: number, b: number, c: number, d: number, e: number): void => {
    const dirX = d - b, lenX = Math.max(Math.abs(d - c), Math.abs(c - b)); const sLX = Math.min(1, Math.max(0, Math.abs(dirX) / Math.max(lenX, 1 / 65536)));
    dx += dirX * w; len += sLX * sLX * w;
    const dirY = e - a, lenY = Math.max(Math.abs(e - c), Math.abs(c - a)); const sLY = Math.min(1, Math.max(0, Math.abs(dirY) / Math.max(lenY, 1 / 65536)));
    dy += dirY * w; len += sLY * sLY * w;
  };
  edge((1 - fx) * (1 - fy), L(sB), L(sE), L(sF), L(sG), L(sJ));
  edge(fx * (1 - fy), L(sC), L(sF), L(sG), L(sH), L(sK));
  edge((1 - fx) * fy, L(sF), L(sI), L(sJ), L(sK), L(sN));
  edge(fx * fy, L(sG), L(sJ), L(sK), L(sL), L(sO));
  const dirSq = dx * dx + dy * dy; const zro = dirSq < 1 / 32768; const r = 1 / Math.sqrt(Math.max(dirSq, 1 / 32768));
  if (zro) { dx = 1; dy = dy * 1; } else { dx *= r; dy *= r; }
  len = (len * 0.5) ** 2;
  const stretch = (dx * dx + dy * dy) / Math.max(Math.abs(dx), Math.abs(dy));
  const l2x = 1 + (stretch - 1) * len, l2y = 1 - len * 0.5; const lob = 0.5 + (1 / 4 - 0.04 - 0.5) * len; const clp = 1 / lob;
  const aC = [0, 0, 0]; let aW = 0;
  const acc = (ox: number, oy: number, c: [number, number, number]): void => {
    const ofx = ox - fx, ofy = oy - fy; const vx = ofx * dx + ofy * dy, vy = -ofx * dy + ofy * dx;
    const sx = vx * l2x, sy = vy * l2y; const d2 = Math.min(sx * sx + sy * sy, clp);
    const wB = d2 * 0.4 - 1, wA = d2 * lob - 1; const w = (wB * wB * (25 / 16) - (25 / 16 - 1)) * (wA * wA);
    aC[0] += c[0] * w; aC[1] += c[1] * w; aC[2] += c[2] * w; aW += w;
  };
  acc(0, -1, sB); acc(1, -1, sC); acc(-1, 0, sE); acc(0, 0, sF); acc(1, 0, sG); acc(2, 0, sH);
  acc(-1, 1, sI); acc(0, 1, sJ); acc(1, 1, sK); acc(2, 1, sL); acc(0, 2, sN); acc(1, 2, sO);
  const out: [number, number, number] = [0, 0, 0];
  for (let k = 0; k < 3; k++) {
    const lo = Math.min(sF[k], sG[k], sJ[k], sK[k]), hi = Math.max(sF[k], sG[k], sJ[k], sK[k]);
    out[k] = Math.min(hi, Math.max(lo, aC[k] / aW));
  }
  return out;
}

export interface RCASUniforms {
  sharpness: ReturnType<typeof uniform>;
  invSrcSize: ReturnType<typeof uniform>;
}

/** AMD FSR1 `FSR_RCAS_LIMIT` = 0.25 − 1/16: max lobe magnitude before sharpness. */
export const RCAS_LOBE_LIMIT = 0.25 - 1 / 16;

/**
 * CPU mirror of the RCAS lobe / unsharp denominator (exported for tests —
 * the TSL kernel above uses the identical expression).
 */
export function rcasLobeCpu(hitMin: number, hitMax: number, sharpness: number): { lobe: number; denom: number } {
  const raw = -Math.max(-hitMin, hitMax);
  const lobe = Math.max(-RCAS_LOBE_LIMIT, Math.min(0, raw)) * sharpness;
  return { lobe, denom: 1 + 4 * lobe };
}

export interface RCASNodeResult {
  node: unknown;
  uniforms: RCASUniforms;
}

/**
 * Build the RCAS sharpening node.
 *
 * Reads `srcColor` and applies a 3×3 unsharp filter with neighborhood-
 * clamped sharpening — bright/dark regions are protected from amplifying
 * noise.
 *
 * The output is clamped to the [min, max] of the 4-neighborhood to
 * prevent overshoot.
 */
export function createRCASNode(args: {
  srcColor: unknown;
  srcWidth: number;
  srcHeight: number;
  sharpness: number;
}): RCASNodeResult {
  const sharpnessU = uniform(args.sharpness);
  const invSrcSizeU = uniform(vec2(1 / args.srcWidth, 1 / args.srcHeight));

  const rcasFn: any = Fn(() => {
    const uv = screenUV;
    const c: any = sampleAt(args.srcColor, uv);
    const n: any = sampleAt(args.srcColor, vec2(uv.x, uv.y.sub(invSrcSizeU.y)));
    const s: any = sampleAt(args.srcColor, vec2(uv.x, uv.y.add(invSrcSizeU.y)));
    const e: any = sampleAt(args.srcColor, vec2(uv.x.add(invSrcSizeU.x), uv.y));
    const w: any = sampleAt(args.srcColor, vec2(uv.x.sub(invSrcSizeU.x), uv.y));

    const minRGB = min(min(min(n.rgb, s.rgb), e.rgb), w.rgb);
    const maxRGB = max(max(max(n.rgb, s.rgb), e.rgb), w.rgb);

    // Per-channel lobe weight (use green channel as luma proxy — cheaper than dot)
    const hitMin = min(min(minRGB.g, c.g).div(maxRGB.g.add(float(1e-6))), float(1));
    const hitMax = float(1).sub(max(maxRGB.g, c.g)).div(float(1).sub(minRGB.g).add(float(1e-6)));
    // WHY the clamp: unclamped, lobe = −hitMax·sharpness reaches −sharpness on
    // a dark high-contrast pixel, so `1 + 4·lobe` hit ZERO at the default
    // sharpness (0.25) and went NEGATIVE above it — Inf/inverted sharpening
    // clamped into hard black/white speckle on contrast edges. AMD's RCAS
    // limits the lobe to [−(0.25 − 1/16), 0] before applying sharpness.
    const lobeRaw = max(hitMin.negate(), hitMax).negate();
    const lobe = clamp(lobeRaw, float(-RCAS_LOBE_LIMIT), float(0)).mul(sharpnessU);

    const result = n.rgb.add(s.rgb).add(e.rgb).add(w.rgb).mul(lobe).add(c.rgb);
    const denom = float(1).add(lobe.mul(4));
    const sharpened = vec3(
      clamp(result.x.div(denom), minRGB.x, maxRGB.x),
      clamp(result.y.div(denom), minRGB.y, maxRGB.y),
      clamp(result.z.div(denom), minRGB.z, maxRGB.z),
    );
    return vec4(sharpened, c.a);
  });

  return {
    node: rcasFn(),
    uniforms: { sharpness: sharpnessU, invSrcSize: invSrcSizeU },
  };
}
